import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFinding, patchFinding, type FindingStatus } from '../api/findings';
import { startTriageSession } from '../api/triage';
import { formatDate, relativeTime } from '../lib/format';

const STATUS_OPTIONS: FindingStatus[] = [
  'open', 'triaging', 'in_progress', 'pr_open', 'pr_merged', 'resolved', 'dismissed',
];

interface StatusChangeFormProps {
  currentStatus: FindingStatus;
  newStatus: FindingStatus | '';
  patchReason: string;
  isPending: boolean;
  onStatusChange: (s: FindingStatus | '') => void;
  onReasonChange: (r: string) => void;
  onSave: () => void;
}

export function StatusChangeForm({
  currentStatus, newStatus, patchReason, isPending,
  onStatusChange, onReasonChange, onSave,
}: StatusChangeFormProps): React.ReactElement {
  return (
    <div className="bg-white border border-border rounded-lg p-4">
      <h2 className="text-sm font-semibold text-text-secondary mb-3">Change Status</h2>
      <div className="flex gap-3">
        <select
          value={newStatus}
          onChange={(e) => onStatusChange(e.target.value as FindingStatus)}
          className="text-sm border border-border rounded-md px-3 py-1.5"
        >
          <option value="">Select new status…</option>
          {STATUS_OPTIONS.filter((s) => s !== currentStatus).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input
          value={patchReason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="Reason for change"
          className="flex-1 text-sm border border-border rounded-md px-3 py-1.5"
        />
        <button
          onClick={onSave}
          disabled={!newStatus || !patchReason.trim() || isPending}
          className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </div>
  );
}

type FindingData = Awaited<ReturnType<typeof getFinding>>;

interface FindingMetaCardProps {
  finding: FindingData;
}

export function FindingMetaCard({ finding }: FindingMetaCardProps): React.ReactElement {
  return (
    <div className="bg-white border border-border rounded-lg p-4 space-y-4">
      <dl className="grid grid-cols-3 gap-4 text-sm">
        <div><dt className="text-text-muted">Status</dt><dd className="mt-0.5 font-medium">{finding.status}</dd></div>
        <div><dt className="text-text-muted">First seen</dt><dd className="mt-0.5">{formatDate(finding.firstSeenAt)}</dd></div>
        <div><dt className="text-text-muted">Affected users</dt><dd className="mt-0.5">{finding.affectedUserIds.length}</dd></div>
        <div><dt className="text-text-muted">Classifier</dt><dd className="mt-0.5 font-mono text-xs">{finding.classifierVersion}</dd></div>
        <div><dt className="text-text-muted">Signature</dt><dd className="mt-0.5 font-mono text-xs">{finding.signature.slice(0, 16)}…</dd></div>
      </dl>
      {finding.prUrl && (
        <p className="text-sm text-text-secondary">
          PR: <a href={finding.prUrl} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">{finding.prUrl}</a> — {finding.prState}
        </p>
      )}
      <div>
        <p className="text-xs font-medium text-text-secondary mb-1">Sample Run IDs</p>
        <ul className="space-y-0.5">
          {finding.sampleRunIds.map((rid) => (
            <li key={rid} className="font-mono text-xs text-text-muted">{rid}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function FindingDetail(): React.ReactElement {
  const { status, id } = useParams<{ status: string; id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [patchReason, setPatchReason] = useState('');
  const [newStatus, setNewStatus] = useState<FindingStatus | ''>('');

  const { data: finding, isLoading, isError } = useQuery({
    queryKey: ['finding', status, id],
    queryFn: () => getFinding(status as FindingStatus, id!),
    enabled: !!status && !!id,
  });

  const patchMut = useMutation({
    mutationFn: ({ ns, reason }: { ns: FindingStatus; reason: string }) =>
      patchFinding(status as FindingStatus, id!, { status: ns }, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['findings'] });
      void queryClient.invalidateQueries({ queryKey: ['finding', status, id] });
    },
  });

  const triageMut = useMutation({
    mutationFn: () => startTriageSession({ findingIds: [id!], allowAutoMerge: false, mode: 'manual' }),
    onSuccess: (session) => { navigate(`/triage-sessions/${session.sessionId}`); },
  });

  if (isLoading) return <p className="text-sm text-text-muted">Loading…</p>;
  if (isError || !finding) return <p className="text-sm text-red-600">Finding not found.</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">{finding.summary}</h1>
          <p className="text-sm text-text-muted mt-0.5">
            {finding.category} · {finding.count} occurrences · last seen {relativeTime(finding.lastSeenAt)}
          </p>
        </div>
        <button
          onClick={() => triageMut.mutate()}
          disabled={triageMut.isPending}
          className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50"
        >
          {triageMut.isPending ? 'Starting…' : 'Start Triage Session'}
        </button>
      </div>
      <FindingMetaCard finding={finding} />
      <StatusChangeForm
        currentStatus={finding.status}
        newStatus={newStatus}
        patchReason={patchReason}
        isPending={patchMut.isPending}
        onStatusChange={setNewStatus}
        onReasonChange={setPatchReason}
        onSave={() => {
          if (!newStatus || !patchReason.trim()) return;
          patchMut.mutate({ ns: newStatus as FindingStatus, reason: patchReason });
        }}
      />
    </div>
  );
}
